import React from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { BRANCH_NAME_MAX_LENGTH, IBranchNameValidationResult, validateBranchName } from "./branch-name-validation";
import "./branching-modal.scss";

export interface ICreateBranchModalBranchOption {
  value: string;
  label: string;
}

export interface ICreateBranchModalProps {
  showModal: boolean;
  branchName: string;
  selectedSourceBranch: string;
  branchOptions: ICreateBranchModalBranchOption[];
  existingBranchNames: readonly string[];
  inProgress: boolean;
  /**
   * Where the commit stands (`KuikaButton` `status`). Given, the commit button carries it —
   * the busy word, then its ✓ or "Try again" — and `inProgress` no longer draws on it.
   */
  status?: KuikaButtonStatus;
  onClose: () => void;
  onBranchNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSourceBranchChange: (value: string | number | Array<string | number> | null) => void;
  onCreateBranch: () => void;
}

const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

const CreateBranchModal: React.FC<ICreateBranchModalProps> = ({
  showModal,
  branchName,
  selectedSourceBranch,
  branchOptions,
  existingBranchNames,
  inProgress,
  status,
  onClose,
  onBranchNameChange,
  onSourceBranchChange,
  onCreateBranch
}) => {
  const validation: IBranchNameValidationResult = validateBranchName(branchName, {
    existingNames: existingBranchNames
  });
  // While the ✓ is up the new branch is already in the list, so the name reads as taken — the
  // check is the answer then, not the rule.
  const isConfirming: boolean = status === "success";
  const showError: boolean = !validation.valid && branchName.length > 0 && !isConfirming;

  const footer = (
    <KModalFooter
      closesOnSuccess
      onCancel={onClose}
      submitLabel="Create"
      submitTestId="createBranchSubmitButton"
      onSubmit={onCreateBranch}
      loading={inProgress}
      status={status}
      statusLabels={CREATE_STATUS_LABELS}
      disabled={!validation.valid && !isConfirming}
    />
  );

  return (
    <KModal showModal={showModal} closeModal={onClose} header="New Branch" footer={footer} width={360} centered>
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="branching" iconType="topbar-icons" intent="create" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>New Branch</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          <div className="kuika-branching-modal__description">
            <KuikaText>Type down the new branch name and select a source down below</KuikaText>
          </div>
          <div className="kuika-branching-modal__form">
            <div className="kuika-branching-modal__form-item">
              <KuikaInput
                data-testid="createBranchNameInput"
                placeholder="Branch Name"
                value={branchName}
                onChange={(e) => onBranchNameChange(e as React.ChangeEvent<HTMLInputElement>)}
                size="lg"
                autoFocus
                maxLength={BRANCH_NAME_MAX_LENGTH}
                error={showError}
              />
              {showError && (
                <div className="kuika-branching-modal__field-error">
                  <KuikaText>{validation.error}</KuikaText>
                </div>
              )}
            </div>
            <div className="kuika-branching-modal__form-item">
              <KuikaSelectbox
                placeholder="Select source branch"
                value={selectedSourceBranch}
                onChange={(e) => onSourceBranchChange(e)}
                options={branchOptions}
                size="lg"
              />
            </div>
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default CreateBranchModal;
